import { MenuItem, Divider } from "@mui/material";
import {
  isVariationOfAll,
  ALL_PROCESS_NAME_DISPLAY,
  ALL_GROUP_NAME_DISPLAY,
  createAllProcessGroup,
} from "./filtersDrawerUtils";
import type { Process, TrendsPageFilterStateType } from "../../../types/types";
import styled from "styled-components/macro";
import { useDispatch } from "react-redux";
import { updateTrendsPageFilterState } from "../../../providers/GlobalStateProvider";
import { isEqual } from "lodash";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useTrendsPageFilterState,
  useProcessesInVisibleGroups,
  useVisibleGroups,
} from "utils/customHooks/genericHooks";

const ProcessGroupTitleStyles = styled(MenuItem)`
  pointer-events: none;
  font-weight: bold !important;
  padding-top: 1.5em !important;
`;

const FilterByProcessDropdownItems = ({ handleClose }) => {
  const dispatch = useDispatch();
  const ckey = useCustomerKey();
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const allProcesses = useProcessesInVisibleGroups();
  const visibleGroups = useVisibleGroups();
  const numProcesses = allProcesses.length;
  const isReady = numProcesses >= 1;

  if (!isReady) {
    return null;
  }

  const processNameList = allProcesses.map(p => p.name);

  // create an "all" process group
  const allProcessGroupCreated = createAllProcessGroup(ckey);
  const allProcessCreated = allProcessGroupCreated.processes[0];

  // if the customer has an "All" group,
  const customersAllProcessGroup = visibleGroups.find(({ longName }) =>
    isVariationOfAll(longName)
  );
  // AND if it actually has all processes in it,
  const doesCustomerAllGroupContainAllProcesses =
    customersAllProcessGroup &&
    customersAllProcessGroup.processes.length === allProcesses.length;
  // use it:
  const allProcessGroupFromCustomer = doesCustomerAllGroupContainAllProcesses
    ? customersAllProcessGroup
    : undefined;

  const isAllSelected =
    trendsPageFilterState.process.name.toLowerCase() === "all";
  // the preferredProcess comes from the store with initial value "All,"
  // but some clients have custom default names for the group that contains
  // all processes

  const selectedProcess = isAllSelected
    ? allProcessCreated
    : trendsPageFilterState.process;

  // add an "all" process group to allow selecting "all" processes
  let processGroups = visibleGroups;
  if (
    !doesCustomerAllGroupContainAllProcesses &&
    !processNameList.includes(ALL_PROCESS_NAME_DISPLAY)
  ) {
    processGroups = [allProcessGroupCreated, ...visibleGroups];
  }

  processGroups = processGroups.filter(
    processGroup =>
      processGroup.processes[0]?.workScheduleConfig?.id ===
      trendsPageFilterState.workScheduleId
  );

  const handleProcessDropdownChange = p => {
    const newProcessFilter: Process = p;
    if (!isEqual(trendsPageFilterState.process, newProcessFilter)) {
      const newFilterState: TrendsPageFilterStateType = {
        ...trendsPageFilterState,
        process: getProcessWithoutVariableFields(newProcessFilter),
        // can only filter by group OR process not both
        group: ALL_GROUP_NAME_DISPLAY,
      };
      dispatch(updateTrendsPageFilterState({ newFilterState }));
    }
  };

  const filteredGroups = allProcessGroupFromCustomer
    ? [allProcessGroupFromCustomer]
    : processGroups.filter(
        ({ longName }) => longName !== ALL_PROCESS_NAME_DISPLAY
      );
  return (
    <>
      <MenuItem
        value={JSON.stringify(
          getProcessWithoutVariableFields(allProcessGroupCreated.processes[0])
        )}
        className={isAllSelected ? "active" : ""}
      >
        All
      </MenuItem>
      {filteredGroups.map(({ longName, processes }) => {
        return [
          <ProcessGroupTitleStyles key={longName}>
            <div className="pnl-title">{longName}</div>
          </ProcessGroupTitleStyles>,
          <Divider key={`${longName}_divider`} />,
          ...processes.map(process => {
            const isSelected = selectedProcess.name === process.name;
            return (
              <MenuItem
                key={`${longName}-${process.name}`}
                value={JSON.stringify(getProcessWithoutVariableFields(process))}
                data-value={process.id}
                onClick={event => {
                  event.stopPropagation();
                  if (selectedProcess.name === process.name) {
                    return;
                  }
                  handleProcessDropdownChange(process);
                  handleClose();
                }}
                className={isSelected ? "active" : ""}
              >
                {process.name}
              </MenuItem>
            );
          }),
        ];
      })}
    </>
  );
};

export default FilterByProcessDropdownItems;

// make all variable data constant (that can change in different configs)
// we use this to match against when searching for that process
// (since we're just looking for the process, we don't care about its current state)
function getProcessWithoutVariableFields(process: Process): Process {
  return {
    id: process.id,
    ckey: process.ckey,
    name: process.name,
    isSelected: true,
    showCountNotMetricOnGroupTile: process.showCountNotMetricOnGroupTile,
    workScheduleConfig: {
      id: process.workScheduleConfig?.id,
    },
    taps: [],
  };
}
